import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Skeleton,
  HStack,
} from '@chakra-ui/react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React from 'react';

import { Bank } from '@finley-technologies/finley-types';
import { ValleyBankCollateralAccount } from '@finley-technologies/valley-bank-utils';

import {
  CsvXlsxDownloadSelect,
  getTanstackVisibleDataForDownload,
} from '~/packages/common-frontend/components/CsvXlsxDownloadSelect';
import { ColumnDisplaySettingsButton } from '~/packages/common-frontend/components/TanStack/ColumnReordering';
import {
  createDefaultTableColumnConfiguration,
  getColumnNames,
  TableColumnConfigurationKey,
  useTableColumnPreferences,
} from '~/packages/common-frontend/hooks/useTableColumnPreferences';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from '~/packages/common-frontend/utils';
import {
  CollateralAccountArrangementGroupNumbers,
  getArrangementGroupNumbers,
} from '~/valleyBank/domains/Collateral/ui/CollateralAccountArrangementGroupNumbers';
import { CollateralAccountLoanAccountNumbers } from '~/valleyBank/domains/Collateral/ui/CollateralAccountLoanAccountNumbers';
import { CustomerSummaryLink } from '~/valleyBank/domains/Customers/hooks/CustomerContext';

const TABLE_KEY: TableColumnConfigurationKey =
  'valleyBankSbl.customerCollateralAccounts';

const DEFAULT_COLUMN_CONFIG = createDefaultTableColumnConfiguration({
  tableKey: TABLE_KEY,
  columns: [
    {
      columnKey: 'accountNumber',
      isVisible: true,
      isFixed: false,
    },
    // @jendevelops deleted accountName since it wasn't populated, but stacey might want it once we figure out what the name is from
    {
      columnKey: 'productName',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'marketValue',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'effectiveAdvanceRate',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'preAllocationLendableValue',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'collateralType',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'loanAccountNumbers',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'arrangementGroupNumbers',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'cifNum',
      isVisible: true,
      isFixed: false,
    },
  ],
});

// fill column defs according to headers map
const COLUMN_DEFS: ColumnDef<ValleyBankCollateralAccount, any>[] = [
  {
    id: 'accountNumber',
    accessorKey: 'accountNumber',
    header: 'Account Number',
  },
  {
    id: 'productName',
    accessorKey: 'productName',
    header: 'Product Name',
  },
  {
    id: 'loanAccountNumbers',
    header: 'Loan Accounts',
    accessorFn: (row) => {
      return (
        row.receivables[0]?.receivableReponseTapeRow.baseFields
          .finley_assigned_to_loan_accounts || 'Unassigned'
      );
    },
    cell: function formatLoanAccountNumbers({
      row: {
        original: { receivables },
      },
    }) {
      return <CollateralAccountLoanAccountNumbers receivables={receivables} />;
    },
  },
  {
    id: 'arrangementGroupNumbers',
    header: 'Arrangement Group',
    accessorFn: (row) => {
      return getArrangementGroupNumbers(row.receivables).join('|');
    },
    cell: function formatArrangementGroupNumbers({
      row: {
        original: { receivables },
      },
    }) {
      return (
        <CollateralAccountArrangementGroupNumbers receivables={receivables} />
      );
    },
  },
  {
    id: 'collateralType',
    header: 'Collateral Type',
    accessorFn: function formatCollateralType(row) {
      return row.receivables[0]?.receivableReponseTapeRow.baseFields
        .collateral_account_type;
    },
    cell: function formatCollateralType({ getValue }) {
      return getValue() || '-';
    },
  },
  {
    id: 'cifNum',
    accessorKey: 'cifNum',
    header: 'CIF Number',
    cell: function formatCifNum({ getValue }) {
      return (
        <CustomerSummaryLink cifnum={getValue()}>
          {getValue()}
        </CustomerSummaryLink>
      );
    },
  },
  {
    id: 'marketValue',
    accessorKey: 'marketValue',
    header: 'Market Value',
    cell: function formatMarketValue({ getValue }) {
      return formatCurrencyAmount(getValue(), Bank.Currency.USD);
    },
  },
  {
    id: 'effectiveAdvanceRate',
    accessorKey: 'effectiveAdvanceRate',
    header: 'Blended Advance Rate',
    cell: function formatEffectiveAdvanceRate({ getValue }) {
      return percentageToPercentageLabel(getValue(), 2);
    },
  },
  {
    id: 'preAllocationLendableValue',
    accessorKey: 'preAllocationLendableValue',
    header: 'Lendable Value',
    cell: function formatPreAllocationLendableValue({ getValue }) {
      return formatCurrencyAmount(getValue(), Bank.Currency.USD);
    },
  },
];

const COLUMN_NAMES = getColumnNames(COLUMN_DEFS);

export const CollateralAccountsTable = ({
  cifNum,
  collateralAccounts,
  isLoading,
  isError,
}: {
  cifNum: string | null;
  collateralAccounts: ValleyBankCollateralAccount[];
  isLoading: boolean;
  isError: boolean;
}) => {
  const {
    columnConfiguration,
    columnOrder,
    columnVisibility,
    updateUserColumnConfiguration,
  } = useTableColumnPreferences({
    tableKey: TABLE_KEY,
    defaultTableColumnConfiguration: DEFAULT_COLUMN_CONFIG,
  });

  const table = useReactTable({
    data: collateralAccounts,
    columns: COLUMN_DEFS,
    state: {
      columnOrder,
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
  });

  const csvDownload = getTanstackVisibleDataForDownload({
    tableInstance: table,
  });

  return (
    <>
      <HStack alignSelf="flex-end">
        <ColumnDisplaySettingsButton
          initialColumns={columnConfiguration}
          updateColumnConfigs={updateUserColumnConfiguration}
          columnNames={COLUMN_NAMES}
        />
        <CsvXlsxDownloadSelect
          dataRows={csvDownload.dataRows}
          headers={csvDownload.headers}
          filenamePrefix={`collateral-accounts-cif-${cifNum}`}
        />
      </HStack>
      <TableContainer
        mt="32px"
        // maxH required for table top sticky header to work
        // since position:sticky attaches to the nearest ancestor with a scrolling mechanism
        // https://stackoverflow.com/questions/70770027/table-with-sticky-header-and-horizontal-scroll
        overflowX="auto"
        overflowY="auto"
        position="relative"
        id="receivables-dashboard-main-table"
        w="100%"
      >
        <Table id="valley-bank-customer-collateral-accounts-table">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <Th key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          {isLoading && !isError && (
            <Tbody>
              {Array.from(Array(10).keys()).map((i) => (
                <Tr key={i}>
                  {table.getVisibleFlatColumns().map((column) => (
                    <Td
                      key={`${column.id}+${i}`}
                      padding="8px 16px"
                      height="56px"
                    >
                      <Skeleton
                        h="100%"
                        w={
                          column.columnDef.header === 'Borrower'
                            ? '400px'
                            : undefined
                        }
                      />
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          )}
          {!isLoading && !isError && (
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <Tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      key={cell.id}
                      textAlign={cell.column.columnDef.meta?.style?.textAlign}
                      padding={cell.column.columnDef.meta?.style?.padding}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          )}
        </Table>
      </TableContainer>
    </>
  );
};
